import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FilterField, FilterValues } from '../types';
import { toDateOnlyString } from '../fields/dateRangePresets';
import { unmaskDocumentSearchValues } from '../fields/searchType';

const isEmptyValue = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (typeof value === 'boolean' && value === false) return true;
  return false;
};

const serializeValue = (value: any): string => {
  if (value instanceof Date) {
    return toDateOnlyString(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return String(value);
};

const deserializeValue = (
  key: string,
  raw: string,
  fields: FilterField[],
  initialValues: FilterValues
): any => {
  const dateField = fields.find(
    field =>
      field.kind === 'dateRange' &&
      (field.startName === key || field.endName === key)
  );
  if (dateField) {
    return raw;
  }

  const checkboxField = fields.find(
    field => field.kind === 'checkbox' && field.name === key
  );
  if (checkboxField) {
    return raw === 'true';
  }

  const multiSelectField = fields.find(
    field => field.kind === 'multiSelect' && field.name === key
  );
  if (multiSelectField) {
    return raw
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  const initial = initialValues[key];
  if (typeof initial === 'boolean') {
    return raw === 'true';
  }

  return raw;
};

const buildDefaultValues = (
  fields: FilterField[],
  initialValues: FilterValues = {}
): FilterValues => {
  const defaults: FilterValues = { ...initialValues };

  fields.forEach(field => {
    if (field.kind === 'search') {
      const typeName = field.typeName || 'type';
      const argumentName = field.argumentName || 'argument';
      if (defaults[typeName] === undefined) {
        defaults[typeName] = field.defaultType || field.types[0]?.value || '';
      }
      if (defaults[argumentName] === undefined) {
        defaults[argumentName] = '';
      }
      return;
    }

    if (field.kind === 'dateRange') {
      if (defaults[field.startName] === undefined) defaults[field.startName] = '';
      if (defaults[field.endName] === undefined) defaults[field.endName] = '';
      return;
    }

    if (field.kind === 'checkbox') {
      if (defaults[field.name] === undefined) defaults[field.name] = false;
      return;
    }

    if (field.kind === 'multiSelect') {
      if (defaults[field.name] === undefined) defaults[field.name] = [];
      return;
    }

    if (defaults[field.name] === undefined) {
      defaults[field.name] = '';
    }
  });

  return defaults;
};

const getManagedKeys = (fields: FilterField[]): string[] => {
  const keys: string[] = [];
  fields.forEach(field => {
    if (field.kind === 'search') {
      keys.push(field.typeName || 'type', field.argumentName || 'argument');
      return;
    }
    if (field.kind === 'dateRange') {
      keys.push(field.startName, field.endName);
      return;
    }
    keys.push(field.name);
  });
  return Array.from(new Set(keys));
};

const parseUrlValues = (
  search: string,
  fields: FilterField[],
  initialValues: FilterValues,
  namespace?: string
): FilterValues => {
  const params = new URLSearchParams(search);
  const defaults = buildDefaultValues(fields, initialValues);
  const managedKeys = getManagedKeys(fields);
  const fromUrl: FilterValues = {};

  managedKeys.forEach(key => {
    const paramKey = namespace ? `${namespace}.${key}` : key;
    const raw = params.get(paramKey);
    if (raw === null) return;
    fromUrl[key] = deserializeValue(key, raw, fields, defaults);
  });

  return { ...defaults, ...fromUrl };
};

const writeUrlValues = (
  history: ReturnType<typeof useHistory>,
  location: ReturnType<typeof useLocation>,
  values: FilterValues,
  fields: FilterField[],
  namespace?: string,
  defaults?: FilterValues
) => {
  const params = new URLSearchParams(location.search);
  const managedKeys = getManagedKeys(fields);

  managedKeys.forEach(key => {
    const paramKey = namespace ? `${namespace}.${key}` : key;
    params.delete(paramKey);
  });

  managedKeys.forEach(key => {
    const value = values[key];
    const defaultValue = defaults ? defaults[key] : undefined;

    if (isEmptyValue(value)) return;

    // Don't put default search type alone in URL without argument
    if (key === 'type' || (namespace && key.endsWith('.type'))) {
      const argumentKey = 'argument';
      if (isEmptyValue(values[argumentKey]) && value === defaultValue) return;
    }

    if (Array.isArray(value) && Array.isArray(defaultValue)) {
      if (JSON.stringify(value) === JSON.stringify(defaultValue)) return;
    } else if (value === defaultValue && typeof value !== 'boolean') {
      return;
    }

    const paramKey = namespace ? `${namespace}.${key}` : key;
    params.set(paramKey, serializeValue(value));
  });

  const nextSearch = params.toString();
  const currentSearch = location.search.startsWith('?')
    ? location.search.slice(1)
    : location.search;

  if (nextSearch === currentSearch) return;

  history.replace({
    pathname: location.pathname,
    search: nextSearch ? `?${nextSearch}` : '',
    state: location.state
  });
};

export type UseFilterStateOptions = {
  fields: FilterField[];
  initialValues?: FilterValues;
  syncToUrl?: boolean;
  urlNamespace?: string;
  onSearch: (values: FilterValues) => void;
  onClear?: () => void;
};

export const useFilterState = ({
  fields,
  initialValues = {},
  syncToUrl = true,
  urlNamespace,
  onSearch,
  onClear
}: UseFilterStateOptions) => {
  const history = useHistory();
  const location = useLocation();
  const defaults = useMemo(
    () => buildDefaultValues(fields, initialValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, JSON.stringify(initialValues)]
  );

  const hydratedFromUrl = useRef(false);
  const [draftValues, setDraftValues] = useState<FilterValues>(() =>
    syncToUrl
      ? parseUrlValues(location.search, fields, initialValues, urlNamespace)
      : defaults
  );
  const [appliedValues, setAppliedValues] = useState<FilterValues>(() =>
    syncToUrl
      ? parseUrlValues(location.search, fields, initialValues, urlNamespace)
      : defaults
  );

  useEffect(() => {
    if (!syncToUrl || hydratedFromUrl.current) return;
    hydratedFromUrl.current = true;
    const fromUrl = parseUrlValues(
      location.search,
      fields,
      initialValues,
      urlNamespace
    );
    setDraftValues(fromUrl);
    setAppliedValues(fromUrl);

    const hasUrlFilters = getManagedKeys(fields).some(key => {
      const paramKey = urlNamespace ? `${urlNamespace}.${key}` : key;
      return new URLSearchParams(location.search).has(paramKey);
    });

    if (hasUrlFilters) {
      onSearch(unmaskDocumentSearchValues(fromUrl, fields));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setValue = useCallback((name: string, value: any) => {
    setDraftValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const setValues = useCallback((next: FilterValues) => {
    setDraftValues(prev => ({ ...prev, ...next }));
  }, []);

  const applySearch = useCallback(
    (values?: FilterValues) => {
      const next = values || draftValues;
      const forApi = unmaskDocumentSearchValues(next, fields);
      setDraftValues(next);
      setAppliedValues(next);
      if (syncToUrl) {
        writeUrlValues(history, location, forApi, fields, urlNamespace, defaults);
      }
      onSearch(forApi);
    },
    [draftValues, syncToUrl, history, location, fields, urlNamespace, defaults, onSearch]
  );

  const clearAll = useCallback(() => {
    const cleared: FilterValues = { ...defaults };
    setDraftValues(cleared);
    setAppliedValues(cleared);
    if (syncToUrl) {
      writeUrlValues(history, location, cleared, fields, urlNamespace, defaults);
    }
    onSearch(cleared);
    if (onClear) onClear();
  }, [
    defaults,
    syncToUrl,
    history,
    location,
    fields,
    urlNamespace,
    onSearch,
    onClear
  ]);

  const clearAdvanced = useCallback(() => {
    const next = { ...draftValues };
    fields
      .filter(field => field.advanced && !field.hidden)
      .forEach(field => {
        if (field.kind === 'dateRange') {
          next[field.startName] = defaults[field.startName];
          next[field.endName] = defaults[field.endName];
          return;
        }
        if (field.kind === 'search') {
          next[field.typeName || 'type'] = defaults[field.typeName || 'type'];
          next[field.argumentName || 'argument'] =
            defaults[field.argumentName || 'argument'];
          return;
        }
        next[field.name] = defaults[field.name];
      });
    setDraftValues(next);
  }, [draftValues, fields, defaults]);

  const removeAppliedField = useCallback(
    (field: FilterField) => {
      const next = { ...appliedValues };
      if (field.kind === 'dateRange') {
        next[field.startName] = defaults[field.startName];
        next[field.endName] = defaults[field.endName];
      } else if (field.kind === 'search') {
        next[field.typeName || 'type'] = defaults[field.typeName || 'type'];
        next[field.argumentName || 'argument'] =
          defaults[field.argumentName || 'argument'];
      } else if (field.kind === 'checkbox') {
        next[field.name] = false;
      } else if (field.kind === 'multiSelect') {
        next[field.name] = Array.isArray(defaults[field.name])
          ? defaults[field.name]
          : [];
      } else {
        next[field.name] = defaults[field.name] !== undefined ? defaults[field.name] : '';
      }
      applySearch(next);
    },
    [appliedValues, defaults, applySearch]
  );

  const isFieldActive = useCallback(
    (field: FilterField, values: FilterValues = appliedValues): boolean => {
      if (field.hidden) return false;

      if (field.kind === 'search') {
        const argumentName = field.argumentName || 'argument';
        // Termo de busca: ativo só quando diverge do default (geralmente vazio)
        return (
          !isEmptyValue(values[argumentName]) &&
          values[argumentName] !== defaults[argumentName]
        );
      }

      if (field.kind === 'dateRange') {
        const startChanged =
          !isEmptyValue(values[field.startName]) &&
          values[field.startName] !== defaults[field.startName];
        const endChanged =
          !isEmptyValue(values[field.endName]) &&
          values[field.endName] !== defaults[field.endName];
        return startChanged || endChanged;
      }

      if (field.kind === 'checkbox') {
        return Boolean(values[field.name]) !== Boolean(defaults[field.name]);
      }

      if (field.kind === 'multiSelect') {
        const current = Array.isArray(values[field.name]) ? values[field.name] : [];
        return current.length > 0;
      }

      // select, text, custom
      if (isEmptyValue(values[field.name]) && isEmptyValue(defaults[field.name])) {
        return false;
      }
      return values[field.name] !== defaults[field.name];
    },
    [appliedValues, defaults]
  );

  const advancedActiveCount = useMemo(() => {
    return fields.filter(
      field => field.advanced && !field.hidden && isFieldActive(field, appliedValues)
    ).length;
  }, [fields, appliedValues, isFieldActive]);

  const hasAnyActive = useMemo(() => {
    return fields.some(
      field =>
        !field.hidden &&
        (isFieldActive(field, appliedValues) || isFieldActive(field, draftValues))
    );
  }, [fields, appliedValues, draftValues, isFieldActive]);

  const activeAdvancedFields = useMemo(() => {
    return fields.filter(
      field => field.advanced && !field.hidden && isFieldActive(field, appliedValues)
    );
  }, [fields, appliedValues, isFieldActive]);

  const chipFields = useMemo(() => {
    return fields.filter(field => {
      if (field.hidden || !isFieldActive(field, appliedValues)) return false;
      return field.advanced || field.kind === 'multiSelect';
    });
  }, [fields, appliedValues, isFieldActive]);

  return {
    draftValues,
    appliedValues,
    defaults,
    setValue,
    setValues,
    applySearch,
    clearAll,
    clearAdvanced,
    removeAppliedField,
    isFieldActive,
    advancedActiveCount,
    hasAnyActive,
    activeAdvancedFields,
    chipFields
  };
};

export default useFilterState;
